import React, { useEffect, useRef } from 'react';
import { usePrevious, useWindowSize } from 'react-use';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import classnames from 'classnames';
import { get, map } from 'lodash';

import AvatarLumaBot from '../../components/AvatarLumaBot';
import { useChatContext } from '../../contexts/ChatContext';

import ChatMessage from './ChatMessage';
import MessageWrapper, { MessageWrapperProps, TMessageWrapper } from './MessageWrapper';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflowY: 'auto',
      padding: theme.spacing.unit * 1.5,
      marginBottom: theme.spacing.unit * 1.5,
    },
    rootBlocked: {
      overflowY: 'hidden',
    },
    gap: {
      flex: 1,
    },
  });

export interface MessagesContainerProps
  extends WithStyles<typeof styles>,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  messages: MessageWrapperProps['message'][];
  avatarInbound?: MessageWrapperProps['avatarInbound'];
  onSubmit: MessageWrapperProps['onSubmit'];
  onRevert: (message: TMessageWrapper) => void;
}

const MessagesContainer: React.FC<MessagesContainerProps> = ({
  classes,
  className,
  messages,
  avatarInbound,
  onSubmit,
  onRevert,
}) => {
  const { mode, typing, isLiveChatEnabled } = useChatContext();
  const prevMode = usePrevious(mode);
  const ref = useRef<HTMLDivElement | null>(null);
  const scrollHeight = useRef(0);
  const { height } = useWindowSize();

  // Scroll to the bottom in case `window.innerHeight` changes. E.g.:
  // - Keyboard on Android devices
  // - Close/open the chat
  useEffect(() => {
    if (ref.current) ref.current.scroll({ top: ref.current.scrollHeight });
  }, [height]);

  // Scroll to the bottom when the chat is unblocked. E.g.:
  // - Scheduler message changing from `more details` mode to the chat mode
  useEffect(() => {
    if (ref.current && mode !== 'blocked' && prevMode === 'blocked') {
      setTimeout(() => ref.current!.scroll({ top: ref.current!.scrollHeight }));
    }
  }, [mode, prevMode]);

  // Scroll to the bottom each time there's a new message
  useEffect(() => {
    const refCurrent = ref.current;

    const onDOMNodeInserted = (event: Event) => {
      // WORKAROUND Avoid scrolling when MUI ripple effects are added bc of pressing a button
      // https://app.clickup.com/t/1r04dz8
      if (mode !== 'blocked' && get(event, 'target.nodeName') !== 'SPAN') {
        setImmediate(() => {
          if (refCurrent && refCurrent.scrollHeight !== scrollHeight.current) {
            scrollHeight.current = refCurrent.scrollHeight;
            refCurrent.scroll({ top: refCurrent.scrollHeight, behavior: 'smooth' });
          }
        });
      }
    };

    if (refCurrent) refCurrent.addEventListener('DOMNodeInserted', onDOMNodeInserted);
    return () => {
      if (refCurrent) refCurrent.removeEventListener('DOMNodeInserted', onDOMNodeInserted);
    };
  }, [mode]);

  return (
    <div ref={ref} className={classnames(classes.root, { [classes.rootBlocked]: mode === 'blocked' }, className)}>
      <div className={classes.gap} />
      {map(messages, (message) => (
        <MessageWrapper
          key={message._id}
          message={message}
          avatarInbound={message.user ? <AvatarLumaBot size="xxsmall" user={message.user} /> : avatarInbound}
          onSubmit={onSubmit}
          includeRevert={message.metadata.includeRevert}
          includeTimestamp={message.type === 'outbound' && message.metadata.isLastMessage}
          onRevert={() => onRevert(message)}
        />
      ))}
      {typing && (
        <ChatMessage
          variant="typing"
          avatarInbound={isLiveChatEnabled ? <AvatarLumaBot size="xxsmall" user="force" /> : avatarInbound}
        />
      )}
    </div>
  );
};

export default withStyles(styles, { name: 'MessagesContainer' })(MessagesContainer);
